import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import PendoButton from '../../components/button/pendo-button.vue.js';
import PendoCheckboxOption from '../../components/multiselect/option-types/pendo-checkbox-option.vue.js';
import PendoDataMeasureTrigger from '../../components/multiselect/trigger-types/pendo-data-measure-trigger.vue.js';
import PendoLoadingIndicator from '../../components/loading-indicator/pendo-loading-indicator.vue.js';
import PendoMultiselect from '../../components/multiselect/pendo-multiselect.vue.js';
import PendoActionsOption from '../../components/multiselect/option-types/pendo-actions-option.vue.js';
import PendoInput from '../../components/input/pendo-input.vue.js';
import PendoIcon from '../../components/icon/pendo-icon.vue.js';
import get from 'lodash/get.js';

var script = {
  name: 'PendoMultiselectFilter',
  components: {
    PendoButton,
    PendoCheckboxOption,
    PendoDataMeasureTrigger,
    PendoLoadingIndicator,
    PendoMultiselect,
    PendoActionsOption,
    PendoInput,
    PendoIcon
  },
  props: {
    entityName: {
      type: String,
      required: true
    },
    emptyLabel: {
      type: String,
      default: undefined
    },
    groupOptionsKey: {
      type: String,
      default: undefined
    },
    groupLabelKey: {
      type: String,
      default: undefined
    },
    emptyOption: {
      type: Object,
      default: null
    },
    labelKey: {
      type: String,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    },
    menuWidth: {
      type: Number,
      default: 0
    },
    options: {
      type: Array,
      default: () => []
    },
    searchPlaceholder: {
      type: String,
      default: undefined
    },
    showAction: {
      type: Boolean,
      default: false
    },
    showAllLabel: {
      type: Boolean,
      default: true
    },
    showSearch: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      default: () => []
    },
    autofocusSearch: {
      type: Boolean,
      default: true
    },
    groupSearch: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    prefixIcon: {
      type: String,
      default: null
    },
    allLabel: {
      type: String,
      default: null
    },
    alwaysShowLength: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    showSelectedValues: {
      type: Boolean,
      default: false
    },
    useDataMeasureTrigger: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      allOptions: [],
      filteredOptions: [],
      useInternalSearch: true
    };
  },
  computed: {
    classNames() {
      const classNames = [`multiselect-filter-${this.entityName.toLowerCase().replace(/[^A-Za-z]+/, '-')}`];
      if (this.value.length === 0) {
        classNames.push('inactive');
      }
      return classNames;
    },
    label() {
      const len = this.value.length;
      if (this.options[0] && this.options[0].type === 'metaGroup') {
        let optionsLen = 0;
        if (this.emptyOption) {
          optionsLen++;
        }
        this.options.forEach(option => {
          optionsLen += option[this.groupOptionsKey].length;
        });
        if (this.showAllLabel && (len === 0 || len === optionsLen)) {
          return this.allLabel || `All ${this.entityName}`;
        }
      } else if (this.showAllLabel && (len === 0 || len === this.options.length && !this.alwaysShowLength)) {
        return this.allLabel || `All ${this.entityName}`;
      }
      if (!this.showAllLabel && len === 0) {
        return this.entityName;
      }
      if (len === 1) {
        return this.getLabel(this.value[0]);
      }
      return `${this.entityName} (${len})`;
    },
    showClearAction() {
      return !!this.value.length || this.showSearch;
    },
    noDataText() {
      return `No ${this.emptyLabel || this.entityName} Found`;
    },
    groupOptionsKeyLocal() {
      return this.groupOptionsKey || 'options';
    }
  },
  watch: {
    options: {
      immediate: true,
      handler(value) {
        this.allOptions = value;
        this.filteredOptions = value;
      }
    }
  },
  methods: {
    close() {
      this.$refs.menu.$refs.core.deactivate();
    },
    handleSearch(search, updateInputValue) {
      let useInternalSearch = true;
      if (this.groupSearch && search.includes(':')) {
        const {
          category,
          label
        } = this.parseInputValue(search);
        let options;
        const existingCategory = this.findOptionEqualToInputValue(this.allOptions, category);
        if (existingCategory) {
          useInternalSearch = false;
          const existingCategoryOptionKey = get(existingCategory, this.groupOptionsKeyLocal);
          if (existingCategoryOptionKey) {
            options = [_objectSpread2(_objectSpread2({}, existingCategory), {}, {
              [this.groupOptionsKeyLocal]: existingCategoryOptionKey.filter(option => this.getLabel(option).toLowerCase().includes(label.toLowerCase()))
            })];
          } else {
            options = [existingCategory];
          }
        }
        this.filteredOptions = options;
      } else {
        this.filteredOptions = this.allOptions;
        updateInputValue(search);
      }
      this.useInternalSearch = useInternalSearch;
    },
    getLabel(option) {
      return option[this.labelKey] || option.label || option;
    },
    getGroupLabel(groupOption) {
      return groupOption[this.groupLabelKey] || groupOption.label;
    },
    selectAll(options) {
      if (options[0] && options[0].type === 'metaGroup') {
        let ungroupedOptions = [this.emptyOption];
        options.forEach(group => {
          if (group[this.groupOptionsKey]) {
            ungroupedOptions = [...ungroupedOptions, ...group[this.groupOptionsKey]];
          }
        });
        this.$emit('input', ungroupedOptions);
      } else {
        this.$emit('input', options);
      }
    },
    setValue(value) {
      this.$emit('input', value);
      this.filteredOptions = this.allOptions;
    },
    onEdit(item) {
      this.$emit('edit', item);
    },
    onDelete(item) {
      this.$emit('delete', item);
    },
    onOpen() {
      this.$emit('open');
    },
    parseInputValue(inputValue) {
      if (inputValue && inputValue.includes(':')) {
        const [category, label] = inputValue.split(':');
        return {
          category: category.trim(),
          label: label.trim()
        };
      }
      return {
        label: inputValue.trim()
      };
    },
    findOptionEqualToInputValue(options, inputValue) {
      if (!options.length) {
        return undefined;
      }
      return options.find(option => this.getGroupLabel(option).toLowerCase() === inputValue.toLowerCase());
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-multiselect-filter.vue2.js.map
